import { useTheme } from '@daily/shared/contexts/Theme';
import { pxToRem } from '@daily/shared/lib/pxToRem';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { useCallConfig } from '../../hooks/useCallConfig';
import {
  EmojiReaction,
  useEmojiReactions,
} from '../../hooks/useEmojiReactions';
import { EMOJI_REACTION_ANIMATION_DURATION } from '../../lib/constants';
import { useBreakoutSessionState } from '../BreakoutRooms/hooks/useBreakoutSessionState';

interface Props {
  animation?: 'fadeIn' | 'flyUp' | 'local';
  emojiSize?: number;
  exclusiveEmoji?: string;
  sessionIds?: string[];
}

export const EmojiReactions: React.FC<React.PropsWithChildren<Props>> = memo(
  ({
    animation = 'flyUp',
    emojiSize = 42,
    exclusiveEmoji = '',
    sessionIds = [],
  }) => {
    const { enableEmojiReactions } = useCallConfig();
    const { currentEmojiReaction } = useEmojiReactions();
    const { zIndex } = useTheme();
    const { currentBreakoutRoom } = useBreakoutSessionState();

    const contentRef = useRef<HTMLDivElement>(null);
    const prevReactionIdRef = useRef<string>(null);

    const initTime = useMemo(() => new Date(), []);

    const shouldRenderReaction = useCallback(
      (id: string) => {
        return (
          currentEmojiReaction?.room === currentBreakoutRoom.id &&
          prevReactionIdRef.current !== currentEmojiReaction?.id &&
          (!exclusiveEmoji || exclusiveEmoji === currentEmojiReaction?.emoji) &&
          (!sessionIds.length || sessionIds.some((sid) => sid === id))
        );
      },
      [
        currentBreakoutRoom.id,
        currentEmojiReaction?.emoji,
        currentEmojiReaction?.id,
        currentEmojiReaction?.room,
        exclusiveEmoji,
        sessionIds,
      ]
    );

    const getCalculatedStyles = useCallback(
      (reaction: EmojiReaction) => {
        // set style rules
        const setStyles = (config: {
          animationDuration: number;
          animationName: string;
          animationTimingFunction: string;
          bottom?: number;
          left?: number;
        }) =>
          `animation-duration: ${config.animationDuration}ms; animation-name:${
            config.animationName
          }; animation-timing-function: ${config.animationTimingFunction}; ${
            config.bottom ? `bottom: ${config.bottom}px;` : ''
          } ${
            config.left ? `left: ${config.left}px;` : ''
          } font-size: ${pxToRem(emojiSize)};`;

        const defaultReactionItemStyleConfig = {
          animationDuration: EMOJI_REACTION_ANIMATION_DURATION,
          animationName: animation,
          animationTimingFunction: 'ease-out',
        };

        let reactionItemStyleText = '';

        // set animation-specific style rules
        switch (animation) {
          case 'fadeIn':
            reactionItemStyleText = setStyles(defaultReactionItemStyleConfig);
            break;
          case 'flyUp':
            reactionItemStyleText = setStyles({
              ...defaultReactionItemStyleConfig,
              left: reaction.positionOffset,
            });
            break;
          case 'local':
            reactionItemStyleText = setStyles({
              ...defaultReactionItemStyleConfig,
              animationDuration: 1000,
              animationTimingFunction: 'cubic-bezier(0.25, 1, 0.5, 1)',
            });
            break;
        }

        return reactionItemStyleText;
      },
      [animation, emojiSize]
    );

    /**
     * Inject reactions directly via DOM api.
     * This helps to circumvent side effects when views change while reactions
     * are still kept in state and supposed to animate.
     */
    useEffect(() => {
      if (
        currentEmojiReaction &&
        currentEmojiReaction.created > initTime &&
        shouldRenderReaction(currentEmojiReaction.sessionId)
      ) {
        const reactionItemStyleText = getCalculatedStyles(currentEmojiReaction);

        const emojiElement = document.createElement('div');
        emojiElement.className = 'emoji';
        emojiElement.innerHTML = currentEmojiReaction.emoji;

        const reactionElement = document.createElement('div');
        reactionElement.className = 'emoji-reaction-item';
        reactionElement.id = currentEmojiReaction.id;
        reactionElement.style.cssText = reactionItemStyleText;
        reactionElement.appendChild(emojiElement);

        // clean up DOM element after animation ended
        reactionElement.onanimationend = () => {
          reactionElement.remove();
        };

        contentRef.current?.appendChild(reactionElement);

        // prevReactionIdRef will be checked in shouldRenderReaction().
        // Helps to prevent re-rendering already triggered reactions
        // that are still kept in state while the view is updated.
        prevReactionIdRef.current = currentEmojiReaction.id;
      }
    }, [
      currentEmojiReaction,
      getCalculatedStyles,
      initTime,
      shouldRenderReaction,
    ]);

    if (!enableEmojiReactions) return null;

    return (
      <div className="emoji-reactions" ref={contentRef}>
        <style jsx>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }

            50% {
              opacity: 1;
            }

            to {
              opacity: 0;
            }
          }
          @keyframes flyUp {
            from {
              opacity: 0;
              transform: translateY(0);
            }

            50% {
              opacity: 1;
            }

            to {
              opacity: 0;
              transform: translateY(-230px);
            }
          }
          @keyframes local {
            from {
              transform: scale(1);
              opacity: 1;
            }
            to {
              transform: scale(3);
              opacity: 0;
            }
          }
          .emoji-reactions {
            position: relative;
          }
          .emoji-reactions :global(.emoji-reaction-item) {
            opacity: 0;
            pointer-events: none;
            position: absolute;
            user-select: none;
            z-index: ${zIndex.menu};
          }
          .emoji-reactions :global(.emoji-reaction-item .emoji) {
            bottom: 0;
            left: 0;
            position: absolute;
            transform: translate(-50%, 50%);
          }
        `}</style>
      </div>
    );
  }
);
EmojiReactions.displayName = 'EmojiReactions';
